import { Input, Node, Output } from "rete"
import { NodeData, WorkerInputs, WorkerOutputs } from "rete/types/core/data"
import { booleanDataSocket, categoricalDataSocket, dataSocket, numericDataSocket } from "../socket_types"
import { BaseComponent } from "./base_component"
import { BooleanTileGrid, CategoricalTileGrid, NumericTileGrid } from "../tile_grid"

export class RenameComponent extends BaseComponent {
  constructor() {
    super("Rename")
    this.category = "Conversions"
  }

  async builder(node: Node) {
    node.addInput(new Input("in", "Dataset", dataSocket))
    node.addOutput(new Output("out", "Dataset", dataSocket))

    const syncFromConnections = () => this.syncOutputSocketFromConnections(node)
    this.editor?.on("connectioncreated", syncFromConnections)
    this.editor?.on("connectionremoved", syncFromConnections)
    syncFromConnections()
  }

  private getSocketForInput(input: unknown) {
    const statsType = typeof (input as any)?.getStats === "function" ? (input as any).getStats()?.type : undefined
    if (statsType === "BooleanTileGrid") return booleanDataSocket
    if (statsType === "NumericTileGrid") return numericDataSocket
    if (statsType === "CategoricalTileGrid") return categoricalDataSocket

    if (input instanceof BooleanTileGrid) return booleanDataSocket
    if (input instanceof NumericTileGrid) return numericDataSocket
    if (input instanceof CategoricalTileGrid) return categoricalDataSocket
    return dataSocket
  }

  private syncOutputSocket(editorNode: Node, input: unknown) {
    const output = editorNode.outputs.get("out")
    if (!output) return

    const nextSocket = this.getSocketForInput(input)
    if (output.socket === nextSocket) return

    // Rebuild output so Rete picks up socket-type changes reliably.
    const existingConnections = [...output.connections]
    for (const connection of existingConnections) {
      this.editor?.removeConnection(connection)
    }
    editorNode.removeOutput(output)
    editorNode.addOutput(new Output("out", "Dataset", nextSocket))
    editorNode.update()
  }

  private syncOutputSocketFromConnections(editorNode: Node) {
    const inputPort = editorNode.inputs.get("in")
    const connectedSocket = inputPort?.connections?.[0]?.output?.socket

    // If disconnected, fall back to Any/Dataset socket.
    if (!connectedSocket) {
      this.syncOutputSocket(editorNode, undefined)
      return
    }

    const output = editorNode.outputs.get("out")
    if (!output || output.socket === connectedSocket) return

    const existingConnections = [...output.connections]
    for (const connection of existingConnections) {
      this.editor?.removeConnection(connection)
    }
    editorNode.removeOutput(output)
    editorNode.addOutput(new Output("out", "Dataset", connectedSocket))
    editorNode.update()
  }

  worker(node: NodeData, inputs: WorkerInputs, outputs: WorkerOutputs) {
    const editorNode = this.editor?.nodes.find(n => n.id === node.id)
    if (!editorNode) return

    this.syncOutputSocketFromConnections(editorNode)

    const input = inputs["in"]?.[0] as { name?: string } | undefined
    if (!input) {
      editorNode.meta.errorMessage = "Not enough inputs"
      return
    }

    delete editorNode.meta.errorMessage

    this.syncOutputSocket(editorNode, input)

    const nextName = (editorNode.data.name as string) || "Dataset"
    input.name = nextName
    outputs["out"] = input as any
  }
}
